import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Switch,
  Chip,
  ButtonBase,
  Divider
} from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

import { formatNumericInput } from "@/lib/formatters";

// ---- Types ----------------------------------------------------------------

interface BotSettingsFormProps {
  /** Bot name — seeds the Name field. */
  botName: string;
  /** Allocated amount seed (from the invest-amount step). */
  allocatedAmount: number;
  /** Stocks list label seed (from the template's `assets`). */
  stocksList?: string;
}

const MARKET_START_OPTIONS = [
  "None",
  "At Market Start",
  "15 Minutes After Start",
  "30 Minutes After Start",
  "1 Hour After Start"
];

const MARKET_MIDDAY_OPTIONS = ["None", "At Market Midday"];

const MARKET_CLOSE_OPTIONS = [
  "None",
  "5 Minutes Before Close",
  "15 Minutes Before Close",
  "30 Minutes Before Close"
];

// ---- Component ------------------------------------------------------------

/**
 * Bot settings form for wizard-type bots — the params step of the
 * Marketplace Buy flow (Sergey 2026-05-21). Mirrors the WHB app's bot
 * settings screen: identity, capital, risk guards, and the trading-time
 * windows.
 *
 * Self-contained: it owns its field state. In this build the values
 * aren't persisted downstream — the form is the user-facing settings
 * step; the real bot-config plumbing lands with the bot builder
 * (Phase 5).
 */
export function BotSettingsForm({
  botName,
  allocatedAmount,
  stocksList
}: BotSettingsFormProps) {
  const [name, setName] = useState(botName);
  const [list, setList] = useState(stocksList ?? "Default Symbols List");
  const [allocated, setAllocated] = useState(String(allocatedAmount || ""));
  const [intraday, setIntraday] = useState(false);
  const [skipSafety, setSkipSafety] = useState(false);
  const [maxPositions, setMaxPositions] = useState("20");
  const [orderLimit, setOrderLimit] = useState("100");
  const [marketStart, setMarketStart] = useState(MARKET_START_OPTIONS[0]);
  const [midday, setMidday] = useState(MARKET_MIDDAY_OPTIONS[0]);
  const [beforeClose, setBeforeClose] = useState(MARKET_CLOSE_OPTIONS[1]);
  const [entry, setEntry] = useState(true);
  const [exit, setExit] = useState(true);

  const listOptions = Array.from(
    new Set([list, "Default Symbols List", "Large-Cap US Equities", "Sector ETF Universe"])
  );

  return (
    <Box sx={{ px: 2.5, py: 1 }}>
      <FieldLabel>Name</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <FieldLabel>Symbols</FieldLabel>
      <ButtonBase
        sx={(t) => ({
          width: "100%",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${t.palette.divider}`
        })}
      >
        <Typography variant="body2" color="text.secondary">
          Select symbols
        </Typography>
        <ChevronRightRounded sx={{ fontSize: 20, color: "text.secondary" }} />
      </ButtonBase>

      <FieldLabel>Stocks List</FieldLabel>
      <TextField
        fullWidth
        size="small"
        select
        value={list}
        onChange={(e) => setList(e.target.value)}
      >
        {listOptions.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>

      <FieldLabel>Allocated amount $</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={formatNumericInput(allocated)}
        onChange={(e) => {
          const v = e.target.value.replace(/,/g, "");
          if (/^\d*\.?\d*$/.test(v)) setAllocated(v);
        }}
        inputProps={{ inputMode: "decimal" }}
      />

      <ToggleRow
        label="Intraday Trading"
        checked={intraday}
        onChange={setIntraday}
      />
      <ToggleRow
        label="Skip Budget Safety Rules"
        checked={skipSafety}
        onChange={setSkipSafety}
      />

      <FieldLabel>Max Allowed Open Positions</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={formatNumericInput(maxPositions)}
        onChange={(e) => {
          const v = e.target.value.replace(/,/g, "");
          if (/^\d*$/.test(v)) setMaxPositions(v);
        }}
        inputProps={{ inputMode: "numeric" }}
      />

      <FieldLabel>Base Order Limit $</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={formatNumericInput(orderLimit)}
        onChange={(e) => {
          const v = e.target.value.replace(/,/g, "");
          if (/^\d*\.?\d*$/.test(v)) setOrderLimit(v);
        }}
        inputProps={{ inputMode: "decimal" }}
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Trading Time
      </Typography>

      <FieldLabel>After Market Start</FieldLabel>
      <TextField
        fullWidth
        size="small"
        select
        value={marketStart}
        onChange={(e) => setMarketStart(e.target.value)}
      >
        {MARKET_START_OPTIONS.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>

      <FieldLabel>On Market Midday</FieldLabel>
      <TextField
        fullWidth
        size="small"
        select
        value={midday}
        onChange={(e) => setMidday(e.target.value)}
      >
        {MARKET_MIDDAY_OPTIONS.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>

      <FieldLabel>Before Market Close</FieldLabel>
      <TextField
        fullWidth
        size="small"
        select
        value={beforeClose}
        onChange={(e) => setBeforeClose(e.target.value)}
      >
        {MARKET_CLOSE_OPTIONS.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        <Chip
          label="Entry"
          variant={entry ? "filled" : "outlined"}
          onClick={() => setEntry((v) => !v)}
          sx={{ fontWeight: 700, ...toggleChipSx(entry) }}
        />
        <Chip
          label="Exit"
          variant={exit ? "filled" : "outlined"}
          onClick={() => setExit((v) => !v)}
          sx={{ fontWeight: 700, ...toggleChipSx(exit) }}
        />
      </Stack>
    </Box>
  );
}

// ---- Internals ------------------------------------------------------------

/**
 * Active filled-chip styling. The global theme forces every filled
 * chip to a dark navy background regardless of its `color` prop, so an
 * active `color="primary"` chip would render dark text on dark. Force
 * the mint background + contrast text instead.
 */
function toggleChipSx(active: boolean) {
  return active
    ? {
        bgcolor: "primary.main",
        color: "primary.contrastText",
        "&:hover": { bgcolor: "primary.light" }
      }
    : {};
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mt: 1.5,
        mb: 0.5,
        fontWeight: 700,
        color: "text.secondary"
      }}
    >
      {children}
    </Typography>
  );
}

function ToggleRow({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 1.5 }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </Stack>
  );
}
